import { Box, Fade, Grid, Link, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { fetchData } from "../../common/services/ApiClient";
import {
  bankClassLookup,
  consumerAssistanceLookup,
  formatDate,
  getPrimaryRegulator,
  isDefinedAndTrue,
  setInstitutionStatus,
} from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import { Query, ResultRecord, SucceedingInstitution } from "../../interfaces";
import { selectedStatesAlpha } from "../../refdata/FilterOptions";
import ActiveStatus from "../ActiveStatus";
import LeavingFDICDialog from "../LeavingFDICDialog";
import { CardListCondensedItem } from "../SearchResultCard/SearchResultCard";
import BankFindInstitutionProfile from "./BankFindInstitutionProfile";

import { useLocation } from "react-router-dom";
import LinkToolTip from "../../common/components/LinkToolTip";

const PREFIX = "BankFindInstitutionDetails";

const classes = {
  root: `${PREFIX}-root`,
  subHeader: `${PREFIX}-subHeader`,
  gridItem: `${PREFIX}-gridItem`,
  succeedingInstInfo: `${PREFIX}-succeedingInstInfo`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.root}`]: {
    "& div.MuiTypography-body1": { lineHeight: "1rem" },
    "display": "inline-block",
    "& button#return-to-landing-page-button": {
      marginBottom: "2rem",
    },
    "& a": {
      textTransform: "none",
    },
    "width": "100%",
  },

  [`& .${classes.subHeader}`]: {
    "display": "flex",
    "position": "relative",
    "& > p ": {
      color: theme.palette.primary.main,
      position: "absolute",
      right: 0,
    },
  },

  [`& .${classes.gridItem}`]: {
    "& > div": {
      paddingBottom: "1rem",
      paddingRight: "1rem",
    },
  },

  [`& .${classes.succeedingInstInfo}`]: {
    lineHeight: "1rem",
    marginBottom: "1rem",
  },
}));

interface LocationsByStateProps {
  count: number;
  STALP: string;
}

interface Props {
  results: ResultRecord;
  appState: Query;
}

export default function BankFindInstitutionDetails({
  results,
  appState,
}: Props) {
  const location = useLocation();
  //   const [bankFindDetails, setBankFindDetails] = React.useState<ResultRecord>(
  //     results,
  //   );
  const bankFindDetails = results;
  const [stateTerritories, setStateTerritories] = React.useState({
    resultData: [],
    count: 0,
  });
  const [succeedingInst, setSucceedingInst] =
    React.useState<SucceedingInstitution>({
      resultData: [],
      display: false,
      primaryRegulator: "",
    });

  if (
    succeedingInst.display &&
    location.state &&
    location.state.from === "succeedingInstitutions"
  ) {
    setSucceedingInst({ ...succeedingInst, display: false });
  }

  const consumerAssistanceHoverText =
    "The link is to the consumer help section of the bank's primary Federal regulator, which is the agency mainly responsible for solving a problem or concern.";

  const consumerAssistance = bankFindDetails
    ? consumerAssistanceLookup(bankFindDetails, succeedingInst)
    : undefined;

  let stateCount = 0;
  let territoriesCount = 0;
  stateTerritories.resultData.forEach((data: LocationsByStateProps) => {
    selectedStatesAlpha.forEach((state) => {
      if (state.value === data.STALP) {
        if (state.isState) {
          stateCount += 1;
        } else {
          territoriesCount += 1;
        }
      }
    });
  });

  React.useEffect(() => {
    if (
      bankFindDetails?.ULTCERT &&
      bankFindDetails.ULTCERT.toString() !== bankFindDetails.CERT.toString()
    ) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/institutions?filters=CERT:${
            bankFindDetails.ULTCERT
          }`,
        )
          .then((response) => {
            if (response.data.length > 0 && isMounted) {
              setSucceedingInst({
                resultData: response.data.map((data: any) =>
                  setInstitutionStatus(data),
                ),
                display: true,
                primaryRegulator: response.data[0].REGAGNT,
              });
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [bankFindDetails]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    let isMounted = true;
    if (bankFindDetails?.ACTIVE) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/locations?filters=CERT:${
            bankFindDetails.CERT
          }&agg_by=STALP`,
        )
          .then((response) => {
            if (isMounted) {
              setStateTerritories({
                resultData: response.data,
                count: response.totals.count,
              });
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [bankFindDetails]);

  return (
    <StyledBox>
      <Typography component={"div"} className={classes.subHeader}>
        <Typography variant="h2">Institution Details</Typography>
      </Typography>
      <Grid container spacing={2}>
        <Grid size={3}>
          <ActiveStatus
            institution={bankFindDetails}
            levelTwo
            appState={appState}
          />
        </Grid>
        <Grid size={9}>
          <Grid container>
            <Grid className={classes.gridItem} size={4}>
              <Typography component="div" id="cert">
                <CardListCondensedItem
                  itemLabel="FDIC Cert #"
                  itemValue={bankFindDetails.CERT}
                />
              </Typography>
              <Typography component="div" id="date-established">
                <CardListCondensedItem
                  itemLabel="Established"
                  itemValue={formatDate(bankFindDetails.ESTYMD)}
                />
              </Typography>

              <Typography component="div" id="charter-class">
                <CardListCondensedItem
                  itemLabel="Bank Charter Class"
                  itemValue={bankClassLookup(bankFindDetails.BKCLASS)}
                />
              </Typography>
              <Typography component="div" id="primary-regulator">
                <CardListCondensedItem
                  itemLabel="Primary Federal Regulator"
                  itemValue={getPrimaryRegulator(bankFindDetails.REGAGNT)}
                />
              </Typography>
              {bankFindDetails.CFPBFLAG === 1 && (
                <Typography component="div" id="cfpb">
                  <CardListCondensedItem
                    itemLabel="Secondary Federal Regulator"
                    itemValue="CFPB"
                  />
                </Typography>
              )}
            </Grid>
            <Grid className={classes.gridItem} size={4}>
              <Typography component="div" id="headquarter-address">
                <CardListCondensedItem
                  itemLabel="Corporate Headquarters"
                  itemValue={bankFindDetails.ADDRESS}
                />
                {isDefinedAndTrue(bankFindDetails.ADDRESS2) && (
                  <CardListCondensedItem
                    itemValueId={`address_line_2_${bankFindDetails.CERT}`}
                    itemValue={bankFindDetails.ADDRESS2}
                  />
                )}
                <CardListCondensedItem
                  itemValue={`${bankFindDetails.CITY}, ${bankFindDetails.STALP} ${bankFindDetails.ZIP}`}
                />
              </Typography>
              <>
                {bankFindDetails.WEBADDR ? (
                  <Typography id="primary-website" component="div">
                    <CardListCondensedItem
                      itemLabel="Primary Website"
                      itemValue=""
                    />
                    <LeavingFDICDialog webAddress={bankFindDetails.WEBADDR} />
                  </Typography>
                ) : (
                  ""
                )}
                {bankFindDetails.ACTIVE ? (
                  <Typography component="div" id="locations">
                    <CardListCondensedItem
                      itemLabel="Locations"
                      itemValue={`${(
                        stateTerritories.count -
                        (parseInt(bankFindDetails.OFFFOR)
                          ? parseInt(bankFindDetails.OFFFOR)
                          : 0)
                      ).toLocaleString()} ${
                        stateTerritories.count === 1
                          ? "domestic location"
                          : "domestic locations"
                      }: ${stateCount} ${
                        stateCount === 1 ? "state" : "states"
                      } and ${territoriesCount} ${
                        territoriesCount === 1 ? "territory" : "territories"
                      }.`}
                    />
                    <CardListCondensedItem
                      itemValue={`${
                        bankFindDetails.OFFFOR ? bankFindDetails.OFFFOR : 0
                      } in foreign ${
                        parseInt(bankFindDetails.OFFFOR) === 1
                          ? "location"
                          : "locations"
                      }.`}
                    />
                  </Typography>
                ) : (
                  ""
                )}
              </>
            </Grid>
            <Grid className={classes.gridItem} size={4}>
              {consumerAssistance?.label !== "" ? (
                <Typography component="div" id="consumer-assistance">
                  <CardListCondensedItem
                    itemLabel="Consumer Assistance"
                    itemValue=""
                  />
                  {consumerAssistance?.internal ? (
                    <LinkToolTip
                      title={consumerAssistanceHoverText}
                      enterDelay={250}
                      TransitionComponent={Fade}
                      sx={{ maxWidth: 300 }}
                    >
                      <Link
                        className={"consumer_assistance_link"}
                        href={consumerAssistance.link}
                        underline="always"
                      >
                        {consumerAssistance.label}
                      </Link>
                    </LinkToolTip>
                  ) : (
                    <LeavingFDICDialog
                      webAddress={consumerAssistance?.link}
                      toolTipText={consumerAssistanceHoverText}
                      label={consumerAssistance?.label}
                    />
                  )}
                </Typography>
              ) : (
                ""
              )}
              <Typography component="div" id="fdic-contact">
                <CardListCondensedItem
                  itemLabel="Contact the FDIC"
                  itemValue={""}
                />
                <LinkToolTip
                  sx={{ maxWidth: "100%" }}
                  title="https://ask.fdic.gov/fdicinformationandsupportcenter/s"
                  enterDelay={250}
                  TransitionComponent={Fade}
                >
                  <Link
                    href="https://ask.fdic.gov/fdicinformationandsupportcenter/s"
                    underline="always"
                  >
                    Questions about Bank Information
                  </Link>
                </LinkToolTip>
              </Typography>
            </Grid>
            <Box
              sx={{
                width: "100%",
              }}
            >
              <BankFindInstitutionProfile results={bankFindDetails} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </StyledBox>
  );
}
